import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useLanguage } from '@/contexts/language-context';
import { formatMoney } from '@/lib/utils';
import { employeesQueryKey, listEmployees } from '@/lib/api/employees';
import { listPayrollPayments, payrollPaymentsQueryKey } from '@/lib/api/payroll';

import { PaymentDetailModal } from './payment-detail-modal';

interface Props {
  businessId: string;
}

export function PayrollHistoryList({ businessId }: Props) {
  const { t } = useLanguage();
  const [openPaymentId, setOpenPaymentId] = useState<string | null>(null);

  const paymentsQuery = useQuery({
    queryKey: payrollPaymentsQueryKey(businessId),
    queryFn: () => listPayrollPayments(businessId),
  });

  // All-employees history needs a name per row; reuse the Employees tab's
  // cache rather than adding a new lookup endpoint.
  const employeesQuery = useQuery({
    queryKey: employeesQueryKey(businessId, 'all'),
    queryFn: () => listEmployees(businessId),
  });

  const payments = paymentsQuery.data ?? [];

  if (paymentsQuery.isPending) {
    return <p className="text-sm text-muted-foreground py-10 text-center">{t.loading}</p>;
  }

  if (payments.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-10 text-center">
        {t.payrollHistoryEmpty}
      </p>
    );
  }

  return (
    <>
      <ul className="flex flex-col gap-2">
        {payments.map((payment) => {
          const employeeName =
            employeesQuery.data?.find((e) => e.id === payment.employee_id)?.full_name ??
            payment.employee_id;

          return (
            <li key={payment.id}>
              <button
                type="button"
                onClick={() => setOpenPaymentId(payment.id)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border bg-card text-left hover:shadow-sm transition-shadow"
                data-testid={`payroll-history-row-${payment.id}`}
              >
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{employeeName}</p>
                  <p className="text-xs text-muted-foreground">
                    {t.payrollPaidAt} {new Date(payment.paid_at).toLocaleDateString()}
                  </p>
                </div>
                <span className="text-sm font-semibold shrink-0">
                  {formatMoney(payment.total_amount)}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {openPaymentId && (
        <PaymentDetailModal
          businessId={businessId}
          paymentId={openPaymentId}
          onClose={() => setOpenPaymentId(null)}
        />
      )}
    </>
  );
}
